import {
  Box,
  Skeleton,
  TableCell,
  TablePagination,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { formatVisitDate } from '../../util/dateUtils'

// Shared sizing tokens so dashboard tables and detail lists keep consistent
// row heights and reserved chip slots (prevents layout shift during loading).
export const DASHBOARD_ROW_MIN_HEIGHT = 76
export const DASHBOARD_ROW_PADDING_Y = 1
export const DASHBOARD_CHIP_SLOT_HEIGHT = 24

/** Sortable Date of Service column header, shared by every dashboard table. */
export const DateOfServiceHeaderCell = <TField extends string>({
  qaId,
  field,
  orderBy,
  order,
  onSort,
  width,
}: {
  qaId: string
  field: TField
  orderBy: TField
  order: 'asc' | 'desc'
  onSort: (field: TField) => void
  width?: string
}) => (
  <TableCell sx={width ? { width } : undefined}>
    <TableSortLabel
      data-qa-id={qaId}
      active={orderBy === field}
      direction={orderBy === field ? order : 'asc'}
      onClick={() => onSort(field)}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Date of Service
    </TableSortLabel>
  </TableCell>
)

/**
 * Renders a date of service. Records whose EHR page exposed no parseable date
 * carry none, and say so.
 */
export const DateOfServiceCell = ({ visitDate }: { visitDate?: string }) => {
  const formatted = formatVisitDate(visitDate)
  if (formatted) return <>{formatted}</>

  return (
    <Typography component="span" variant="inherit" color="text.secondary">
      No date of service
    </Typography>
  )
}

const DISPLAYED_ROWS_SX = {
  '& .MuiTablePagination-displayedRows': {
    minWidth: '110px',
    minHeight: '20px',
    display: 'inline-flex',
    alignItems: 'center',
  },
} as const

const formatDisplayedRowsLabel = ({
  from,
  to,
  count,
}: {
  from: number
  to: number
  count: number
}) => {
  const countLabel = count !== -1 ? String(count) : `more than ${to}`
  return (
    <Box component="span" sx={{ lineHeight: '20px', minHeight: '20px', display: 'inline-flex' }}>
      {`${from}-${to} of ${countLabel}`}
    </Box>
  )
}

const renderLoadingDisplayedRowsLabel = () => (
  <Skeleton
    variant="rounded"
    sx={{ display: 'inline-block', verticalAlign: 'middle' }}
    width={90}
    height={20}
  />
)

type DashboardTablePaginationProps = {
  count: number
  rowsPerPage: number
  page: number
  onPageChange: (event: unknown, page: number) => void
  isLoading: boolean
  rowsPerPageOptions?: readonly number[]
  qaId?: string
}

/** Shared server-side table pagination with a loading-aware displayed-rows label. */
export const DashboardTablePagination = ({
  count,
  rowsPerPage,
  page,
  onPageChange,
  isLoading,
  rowsPerPageOptions = [10],
  qaId,
}: DashboardTablePaginationProps) => {
  // While loading, `count` is 0 (data not yet available) but `page` can still be
  // >0 from the current selection. Clamp to the valid range so MUI doesn't warn
  // about an out-of-range page and the prev/next controls stay coherent.
  const lastPageIndex = count > 0 ? Math.ceil(count / rowsPerPage) - 1 : 0
  const safePage = Math.min(Math.max(0, page), lastPageIndex)

  return (
    <TablePagination
      rowsPerPageOptions={[...rowsPerPageOptions]}
      component="div"
      count={count}
      rowsPerPage={rowsPerPage}
      page={safePage}
      onPageChange={onPageChange}
      labelDisplayedRows={isLoading ? renderLoadingDisplayedRowsLabel : formatDisplayedRowsLabel}
      sx={DISPLAYED_ROWS_SX}
      data-qa-id={qaId}
    />
  )
}

/** Placeholder for the list `<Pagination>` control shown while data is loading. */
export const ListPaginationSkeleton = ({ qaId }: { qaId?: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }} data-qa-id={qaId}>
    <Skeleton variant="rounded" width={220} height={32} />
  </Box>
)
